import React, { createContext, useState, useCallback, useMemo, useContext, useRef } from 'react';
import { toast } from 'react-semantic-toasts';
import { componentService } from '../services/ComponentService';
import { mappingTreeService } from '../services/MappingTreeService';
import { componentLabel } from '../features/ComponentManager/componentLabel';

const infoToast = description =>
    toast({
        type: 'info',
        title: 'Mapping',
        description,
        animation: 'fade down',
        time: 5000,
    });

const successToast = description =>
    toast({
        type: 'success',
        title: 'Success',
        description,
        animation: 'fade down',
        time: 5000,
    });

export const MappingTreeContext = createContext();

// Mapping-tree endpoint returns a bare array; the structure-tree endpoint wraps
// its list in { data }. Normalize both to a plain array.
const toItems = res => res?.data ?? (Array.isArray(res) ? res : []);

export const MappingTreeProvider = props => {
    // SOURCE strip (structure tree). columns: { parentId, items }[].
    const [sourceColumns, setSourceColumns] = useState([]);
    const [sourceSelectedPath, setSourceSelectedPath] = useState([]);
    const [sourceLoadingColumnIndex, setSourceLoadingColumnIndex] = useState(null);
    const sourceRequestToken = useRef(0);

    // TARGET strip (mapping tree). Independent state + request token.
    const [targetColumns, setTargetColumns] = useState([]);
    const [targetSelectedPath, setTargetSelectedPath] = useState([]);
    const [targetLoadingColumnIndex, setTargetLoadingColumnIndex] = useState(null);
    const targetRequestToken = useRef(0);

    const [draggedComponent, setDraggedComponent] = useState(null);
    const [draggedMappingNode, setDraggedMappingNode] = useState(null);
    const [addPending, setAddPending] = useState(false);
    const addPendingRef = useRef(false);
    const beginWrite = useCallback(() => {
        addPendingRef.current = true;
        setAddPending(true);
    }, []);
    const endWrite = useCallback(() => {
        addPendingRef.current = false;
        setAddPending(false);
    }, []);

    // Actions intentionally do NOT .catch() — rejections bubble to the page for toasts.

    const loadSourceRoots = useCallback(() => {
        const token = (sourceRequestToken.current += 1);
        setSourceSelectedPath([]);
        setSourceLoadingColumnIndex(null);
        setSourceColumns([]);
        return componentService.getComponentRoots().then(res => {
            if (token !== sourceRequestToken.current) return;
            setSourceColumns([{ parentId: null, items: toItems(res) }]);
        });
    }, []);

    const selectSource = useCallback((component, columnIndex) => {
        const token = (sourceRequestToken.current += 1);

        setSourceSelectedPath(prev => [...prev.slice(0, columnIndex), component]);

        if (!component.has_dependents) {
            setSourceColumns(prev => prev.slice(0, columnIndex + 1));
            setSourceLoadingColumnIndex(null);
            return Promise.resolve();
        }

        // Reuse already-loaded children when re-selecting a node.
        const existingChild = sourceColumns[columnIndex + 1];
        if (existingChild && existingChild.parentId === component.id) {
            setSourceColumns(prev => prev.slice(0, columnIndex + 2));
            setSourceLoadingColumnIndex(null);
            return Promise.resolve();
        }

        setSourceColumns(prev => prev.slice(0, columnIndex + 1));
        setSourceLoadingColumnIndex(columnIndex + 1);

        return componentService
            .getComponentChildren(component.id)
            .then(res => {
                if (token !== sourceRequestToken.current) return;
                setSourceColumns(prev => [
                    ...prev.slice(0, columnIndex + 1),
                    { parentId: component.id, items: toItems(res) },
                ]);
                setSourceLoadingColumnIndex(null);
            })
            .catch(err => {
                // Stale request: the user already moved on — swallow silently.
                if (token !== sourceRequestToken.current) return;
                setSourceLoadingColumnIndex(null);
                throw err;
            });
    }, [sourceColumns]);

    const loadTargetRoots = useCallback(() => {
        const token = (targetRequestToken.current += 1);
        setTargetSelectedPath([]);
        setTargetLoadingColumnIndex(null);
        setTargetColumns([]);
        return mappingTreeService.getMappingRoots().then(res => {
            if (token !== targetRequestToken.current) return;
            setTargetColumns([{ parentId: null, items: toItems(res) }]);
        });
    }, []);

    const selectTarget = useCallback((component, columnIndex) => {
        if (addPendingRef.current) return Promise.resolve();
        const token = (targetRequestToken.current += 1);

        setTargetSelectedPath(prev => [...prev.slice(0, columnIndex), component]);

        // Mapping-tree expansion is driven by childCount (0 = no children).
        if (!component.childCount) {
            setTargetColumns(prev => prev.slice(0, columnIndex + 1));
            setTargetLoadingColumnIndex(null);
            return Promise.resolve();
        }

        const existingChild = targetColumns[columnIndex + 1];
        if (existingChild && existingChild.parentId === component.id) {
            setTargetColumns(prev => prev.slice(0, columnIndex + 2));
            setTargetLoadingColumnIndex(null);
            return Promise.resolve();
        }

        setTargetColumns(prev => prev.slice(0, columnIndex + 1));
        setTargetLoadingColumnIndex(columnIndex + 1);

        return mappingTreeService
            .getMappingChildren(component.id)
            .then(res => {
                if (token !== targetRequestToken.current) return;
                setTargetColumns(prev => [
                    ...prev.slice(0, columnIndex + 1),
                    { parentId: component.id, items: toItems(res) },
                ]);
                setTargetLoadingColumnIndex(null);
            })
            .catch(err => {
                if (token !== targetRequestToken.current) return;
                setTargetLoadingColumnIndex(null);
                throw err;
            });
    }, [targetColumns]);

    const addRoot = useCallback(
        component => {
            if (!component || addPending) return Promise.resolve();

            // Client-side idempotency signal: the backend can't tell inserted vs. existed.
            const rootColumn = targetColumns[0];
            if (rootColumn && rootColumn.items.some(item => item.id === component.id)) {
                infoToast('Already in the mapping tree.');
                return Promise.resolve();
            }

            beginWrite();
            return mappingTreeService
                .addMappingRoot(component.id)
                .then(() => {
                    const token = (targetRequestToken.current += 1);
                    return mappingTreeService.getMappingRoots().then(res => {
                        if (token !== targetRequestToken.current) return;
                        setTargetColumns(prev => {
                            const next = [...prev];
                            next[0] = { parentId: null, items: toItems(res) };
                            return next;
                        });
                        successToast('Added to the mapping tree.');
                    });
                })
                .finally(() => {
                    endWrite();
                });
        },
        [addPending, targetColumns, beginWrite, endWrite]
    );

    const addChild = useCallback(
        (parentComponent, childComponent) => {
            if (!parentComponent || !childComponent || addPending) return Promise.resolve();

            if (parentComponent.id === childComponent.id) {
                infoToast('A component cannot be mapped under itself.');
                return Promise.resolve();
            }

            const existingChildColumn = targetColumns.find(
                col => col.parentId === parentComponent.id
            );
            if (
                existingChildColumn &&
                existingChildColumn.items.some(item => item.id === childComponent.id)
            ) {
                infoToast('Already a child of this component.');
                return Promise.resolve();
            }

            beginWrite();
            return mappingTreeService
                .addMappingChild(parentComponent.id, childComponent.id)
                .then(resp => {
                    if (resp && resp.data === false) {
                        infoToast('Already a child of this component.');
                        return undefined;
                    }
                    const token = (targetRequestToken.current += 1);
                    return mappingTreeService
                        .getMappingChildren(parentComponent.id)
                        .then(res => {
                            if (token !== targetRequestToken.current) return;
                            const items = toItems(res);
                            const parentColumnIndex = targetColumns.findIndex(col =>
                                col.items.some(item => item.id === parentComponent.id)
                            );
                            setTargetColumns(prev => {
                                // Bump the parent row's childCount so its chevron shows.
                                const withCount = prev.map((col, idx) =>
                                    idx === parentColumnIndex
                                        ? {
                                              ...col,
                                              items: col.items.map(item =>
                                                  item.id === parentComponent.id
                                                      ? { ...item, childCount: items.length }
                                                      : item
                                              ),
                                          }
                                        : col
                                );
                                if (parentColumnIndex === -1) return withCount;
                                // Open the parent's child column right away so the new node is visible.
                                return [
                                    ...withCount.slice(0, parentColumnIndex + 1),
                                    { parentId: parentComponent.id, items },
                                ];
                            });
                            if (parentColumnIndex !== -1) {
                                setTargetSelectedPath(prev => [
                                    ...prev.slice(0, parentColumnIndex),
                                    parentComponent,
                                ]);
                            }
                            successToast(`Added to ${componentLabel(parentComponent)}.`);
                        });
                })
                .finally(() => {
                    endWrite();
                });
        },
        [addPending, targetColumns, beginWrite, endWrite]
    );

    const removeNode = useCallback(
        (item, columnIndex, parentId = null) => {
            if (!item || addPending) return Promise.resolve();

            const isRoot = parentId == null;

            beginWrite();
            const op = isRoot
                ? mappingTreeService.removeMappingRoot(item.id)
                : mappingTreeService.removeMappingChild(parentId, item.id);

            return op
                .then(() => {
                    const token = (targetRequestToken.current += 1);
                    const refetch = isRoot
                        ? mappingTreeService.getMappingRoots()
                        : mappingTreeService.getMappingChildren(parentId);
                    return refetch.then(res => {
                        if (token !== targetRequestToken.current) return;
                        const items = toItems(res);
                        const collapseEmptyChild = !isRoot && items.length === 0;
                        const keepCount = collapseEmptyChild ? columnIndex : columnIndex + 1;

                        setTargetSelectedPath(prev =>
                            prev.slice(0, collapseEmptyChild ? columnIndex - 1 : columnIndex)
                        );
                        setTargetColumns(prev =>
                            prev.slice(0, keepCount).map((col, idx) => {
                                if (!collapseEmptyChild && idx === columnIndex) {
                                    return { parentId, items };
                                }
                                if (!isRoot && idx === columnIndex - 1) {
                                    return {
                                        ...col,
                                        items: col.items.map(c =>
                                            c.id === parentId
                                                ? { ...c, childCount: items.length }
                                                : c
                                        ),
                                    };
                                }
                                return col;
                            })
                        );
                        successToast('Removed from the mapping tree.');
                    });
                })
                .finally(() => {
                    endWrite();
                });
        },
        [addPending, beginWrite, endWrite]
    );

    const moveNode = useCallback(
        ({ childId, fromParentId = null, toParentId = null }) => {
            if (!childId || addPending) return Promise.resolve();

            if (toParentId === childId) {
                infoToast('A component cannot be mapped under itself.');
                return Promise.resolve();
            }

            if ((fromParentId ?? null) === (toParentId ?? null)) {
                return Promise.resolve();
            }

            if (toParentId == null) {
                const rootColumn = targetColumns[0];
                if (rootColumn && rootColumn.items.some(item => item.id === childId)) {
                    infoToast('Already in the mapping tree.');
                    return Promise.resolve();
                }
            } else {
                const existingChildColumn = targetColumns.find(col => col.parentId === toParentId);
                if (existingChildColumn && existingChildColumn.items.some(item => item.id === childId)) {
                    infoToast('Already a child of this component.');
                    return Promise.resolve();
                }
            }

            const alreadyThereToast = () =>
                infoToast(
                    toParentId == null
                        ? 'Already in the mapping tree.'
                        : 'Already a child of this component.'
                );

            beginWrite();
            const openColumns = targetColumns;
            return mappingTreeService
                .moveMappingNode({ childId, fromParentId, toParentId })
                .then(resp => {
                    if (resp && resp.data === false) {
                        alreadyThereToast();
                        return;
                    }

                    const token = (targetRequestToken.current += 1);
                    return Promise.all(
                        openColumns.map(col =>
                            (col.parentId == null
                                ? mappingTreeService.getMappingRoots()
                                : mappingTreeService.getMappingChildren(col.parentId)
                            ).then(res => toItems(res))
                        )
                    ).then(itemsPerColumn => {
                        if (token !== targetRequestToken.current) return;
                        const rebuilt = [];
                        for (let i = 0; i < openColumns.length; i += 1) {
                            const items = itemsPerColumn[i];
                            if (i === 0) {
                                rebuilt.push({ parentId: null, items });
                                continue;
                            }
                            const { parentId } = openColumns[i];
                            const parentStillShown = rebuilt[i - 1].items.some(
                                it => it.id === parentId
                            );
                            if (!parentStillShown) break;
                            rebuilt.push({ parentId, items });
                        }
                        setTargetColumns(rebuilt);
                        setTargetSelectedPath(prev => prev.slice(0, rebuilt.length));
                        successToast('Moved in the mapping tree.');
                    });
                })
                .finally(() => {
                    endWrite();
                });
        },
        [addPending, targetColumns, beginWrite, endWrite]
    );

    const values = useMemo(
        () => ({
            // Source strip
            sourceColumns,
            sourceSelectedPath,
            sourceLoadingColumnIndex,
            loadSourceRoots,
            selectSource,
            // Target strip
            targetColumns,
            targetSelectedPath,
            targetLoadingColumnIndex,
            loadTargetRoots,
            selectTarget,
            // Drag-to-add
            draggedComponent,
            setDraggedComponent,
            draggedMappingNode,
            setDraggedMappingNode,
            addPending,
            addRoot,
            addChild,
            removeNode,
            moveNode,
        }),
        [
            sourceColumns,
            sourceSelectedPath,
            sourceLoadingColumnIndex,
            loadSourceRoots,
            selectSource,
            targetColumns,
            targetSelectedPath,
            targetLoadingColumnIndex,
            loadTargetRoots,
            selectTarget,
            draggedComponent,
            draggedMappingNode,
            addPending,
            addRoot,
            addChild,
            removeNode,
            moveNode,
        ]
    );

    return (
        <MappingTreeContext.Provider value={values}>
            {props.children}
        </MappingTreeContext.Provider>
    );
};

export const useMappingTree = () => {
    const context = useContext(MappingTreeContext);
    if (context === undefined) {
        throw new Error('useMappingTree must be used within a MappingTreeProvider component.');
    }
    return context;
};
